'use client'

import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Button } from '../../atoms/button'
import { Icon } from '../../atoms/icon'
import { Link } from '../../atoms/link'
import { Text } from '../../atoms/text'
import { mtc, TypographyStyles } from '../../../utils/style-helpers'
import {
  buttonStyles,
  dismissStyles,
  iconStyles,
  styles,
  textStyles,
  titleStyles,
  wrapperStyles,
} from './styles'

export enum BannerVariants {
  // Inline Banner Variants
  DEFAULT = 'default',
  PRIORITY = 'priority',
  SUCCESS = 'success',
  WARNING = 'warning',
  DANGER = 'danger',

  // Site Banner Variants
  ANNOUNCEMENT = 'announcement',
  URGENT = 'urgent',
}

const icon = {
  default: solid('circle-info'),
  priority: solid('star'),
  success: solid('check'),
  warning: solid('triangle-exclamation'),
  danger: solid('circle-exclamation'),
  announcement: null,
  urgent: solid('triangle-exclamation'),
}

const iconColor = {
  default: 'text-neutral-600',
  priority: 'text-priority-200',
  success: 'text-success-100',
  warning: 'text-warning-200',
  danger: 'text-danger-100',
  announcement: 'text-neutral-900',
  urgent: 'text-neutral-900',
}

export interface BaseBannerProps {
  /** Styling overrides for CTA Button/Link */
  buttonClassName?: string
  /** Styling overrides for banner content */
  className?: string
  /** Call To Action text */
  ctaText?: string
  /** Styling overrides for dismiss button */
  dismissClassName?: string
  /** External link address */
  href?: string
  /** Styling overrides for main banner icon */
  iconClassName?: string
  /** ID - mainly used by Formik */
  id?: string
  /** CTA handler */
  onClick?: () => void
  /** Dismiss banner handler */
  onDismiss?: () => void
  /** Copy for banner */
  text?: string
  /** Styling overrides for main banner copy*/
  textClassName?: string
  /** Title text for Site style banners */
  title?: string
  /** Styling overrides for title text */
  titleClassName?: string
  /** One of generic Status styles or Site style variants*/
  variant?: BannerVariants
  /** Styling overrides for banner wrapper */
  wrapperClassName?: string
  /** Subject to change pending Media Query pattern used */
  betaMediaSize?: 'sm' | 'md' | 'lg'
  /** Loading state for CTA button */
  loading?: boolean
  /** Optional test ID for testing-library */
  ['data-testid']?: string
}

// Disallow passing both href and onClick
type HTMLDivProps = React.HTMLAttributes<HTMLDivElement>

export type BannerProps =
  | (BaseBannerProps & HTMLDivProps & { href: string; onClick?: never })
  | (BaseBannerProps & HTMLDivProps & { onClick: () => void; href?: never })
  | (BaseBannerProps & HTMLDivProps & { href?: never; onClick?: never })

export const Banner = ({
  buttonClassName,
  className,
  ctaText,
  dismissClassName,
  href,
  iconClassName,
  id,
  onClick,
  onDismiss,
  text,
  textClassName,
  title,
  titleClassName,
  wrapperClassName,
  betaMediaSize,
  loading,
  variant = BannerVariants.DEFAULT,
  ...props
}: BannerProps) => {
  const showIcon =
    variant !== BannerVariants.ANNOUNCEMENT && betaMediaSize !== 'sm'
  return (
    <div
      className={mtc(
        wrapperStyles({ variant, betaMediaSize }),
        wrapperClassName
      )}
      id={id}
      {...props}
    >
      <div className={mtc(styles({ variant, betaMediaSize }), className)}>
        {showIcon && icon[variant] && (
          <Icon
            icon={icon[variant]}
            color={iconColor[variant]}
            className={mtc(iconStyles({ variant }), iconClassName)}
          />
        )}

        {title && (
          <Text
            variant={TypographyStyles.LABEL_MD}
            className={mtc(titleStyles(), titleClassName)}
          >
            {title}
          </Text>
        )}

        <Text
          variant={TypographyStyles.BODY_XS}
          className={mtc(textStyles({ variant }), textClassName)}
        >
          {text}

          {href && (
            <Link href={href} className={mtc(buttonStyles(), buttonClassName)}>
              {ctaText}
              <Icon className="ml-[-2px]" icon={regular('arrow-right')} />
            </Link>
          )}

          {onClick && (
            <Button
              className={mtc(buttonStyles(), buttonClassName)}
              onClick={onClick}
              size="sm"
              variant="link"
              loading={loading}
              disabled={loading}
            >
              {ctaText}
              <Icon
                className="pl-1 align-[0.125em]"
                icon={regular('arrow-right')}
              />
            </Button>
          )}
        </Text>
      </div>

      {onDismiss && (
        <Button
          aria-label="Dismiss banner"
          className={mtc(dismissStyles(), dismissClassName)}
          iconRight={solid('close')}
          size="square"
          onClick={onDismiss}
          variant="ghost"
        />
      )}
    </div>
  )
}

Banner.displayName = 'Banner'
